/**
 * Internal Hook: useCreateMeetingMutation
 *
 * Encapsulates the TanStack Query mutation logic for creating a scheduled meeting.
 * It provides a responsive user experience by leveraging optimistic updates:
 * - `onMutate`: Immediately adds a temporary meeting to the cache.
 * - `onSuccess`: Replaces optimistic meeting with real server data.
 * - `onError`: Rolls back the cache to its previous state on failure.
 * - `onSettled`: Refetches the meeting list to ensure data consistency.
 */
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { SCHEDULED_MEETINGS_QUERY_KEYS } from '../helpers';
import type {
  CreateMeetingParams,
  ScheduledMeeting,
  ScheduledMeetingsProviderOptions,
} from '../types';
import useScheduledMeetingsApi from './useScheduledMeetingsApi';

const useCreateMeetingMutation = (options: ScheduledMeetingsProviderOptions) => {
  const queryClient = useQueryClient();
  const api = useScheduledMeetingsApi();

  return useMutation({
    mutationFn: async (params: CreateMeetingParams) => {
      const response = await api.createMeeting(params);
      return response as ScheduledMeeting;
    },
    onMutate: async (newMeetingParams) => {
      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);
      await queryClient.cancelQueries({ queryKey });

      const previousMeetings = queryClient.getQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey);

      const optimisticMeeting: ScheduledMeeting = {
        room: '',
        description: newMeetingParams?.description || 'New Meeting',
        ...newMeetingParams,
        isOptimistic: true,
      };

      queryClient.setQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey, (oldData) => {
        if (!oldData?.pages) {
          return { pages: [[optimisticMeeting]], pageParams: [0] };
        }
        const [firstPage = [], ...restPages] = oldData.pages;
        return {
          ...oldData,
          pages: [[optimisticMeeting, ...firstPage], ...restPages],
        };
      });

      return { previousMeetings };
    },
    onSuccess: (newMeeting, _variables, _context) => {
      const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);

      queryClient.setQueryData<InfiniteData<ScheduledMeeting[]>>(queryKey, (oldData) => {
        if (!oldData?.pages) {
          return oldData;
        }

        const pages = oldData.pages.map((page) => {
          const hasOptimistic = page.some((meeting) => meeting.isOptimistic);

          if (!hasOptimistic) {
            return page;
          }

          return page.map((meeting) => (meeting.isOptimistic ? newMeeting : meeting));
        });

        return { ...oldData, pages };
      });
    },
    onError: (_err, _newMeeting, context) => {
      // Rollback on error
      if (context?.previousMeetings) {
        const queryKey = SCHEDULED_MEETINGS_QUERY_KEYS.list(options.initialQuery);
        queryClient.setQueryData(queryKey, context.previousMeetings);
      }
    },
    onSettled: () => {
      // After the mutation completes (success or error), invalidate the cache
      queryClient.invalidateQueries({ queryKey: SCHEDULED_MEETINGS_QUERY_KEYS.lists() });
    },
  });
};

export default useCreateMeetingMutation;
